/**
 * Identity domain event definitions — catalog 1.1.0 additions
 * (commerce-platform Phase 1, plan §5 / Step 7, task T02).
 *
 * Producer: `auth-service` identity module (phase Step 4). These events cover
 * the D5 identity half — identity creation, credential attachment, and
 * completed password recovery. All are `auditable: true` (phase §5).
 *
 * SECURITY: payloads carry identifiers only — never secrets or PII.
 * Credentials are persisted as hashes (`AuthCredential.secretHash`/`pinHash`)
 * and recovery tokens as single-use hashes; no payload field below may ever
 * hold plaintext or contact details.
 */
import {
  defineEvent,
  asEventRecord,
  optionalNumber,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface IdentityCreatedPayload {
  identityId: string;
}

export interface CredentialAddedPayload {
  identityId: string;
  credentialId: string;
  /**
   * Credential type, one of the `AuthCredential.type` enum values:
   * `password | magic_link | email_otp | sms_otp | social | passkey | kiosk_pin | card`.
   * Never the secret itself — hashes stay server-side.
   */
  credentialType: string;
}

export interface PasswordRecoveryCompletedPayload {
  identityId: string;
  /** ISO-8601 timestamp of the successful recovery confirmation. */
  completedAt: string;
  /** Count of refresh-token families/sessions invalidated by the recovery. */
  sessionsInvalidated?: number;
}

const identityCreatedSchema: EventSchema<IdentityCreatedPayload> = {
  parse(data: unknown): IdentityCreatedPayload {
    const record = asEventRecord(data, 'identities:identity-created');
    return {
      identityId: requireString(record.identityId, 'identityId', 'identities:identity-created'),
    };
  },
};

const credentialAddedSchema: EventSchema<CredentialAddedPayload> = {
  parse(data: unknown): CredentialAddedPayload {
    const record = asEventRecord(data, 'identities:credential-added');
    return {
      identityId: requireString(record.identityId, 'identityId', 'identities:credential-added'),
      credentialId: requireString(record.credentialId, 'credentialId', 'identities:credential-added'),
      credentialType: requireString(record.credentialType, 'credentialType', 'identities:credential-added'),
    };
  },
};

const passwordRecoveryCompletedSchema: EventSchema<PasswordRecoveryCompletedPayload> = {
  parse(data: unknown): PasswordRecoveryCompletedPayload {
    const record = asEventRecord(data, 'identities:password-recovery-completed');
    return {
      identityId: requireString(record.identityId, 'identityId', 'identities:password-recovery-completed'),
      completedAt: requireString(record.completedAt, 'completedAt', 'identities:password-recovery-completed'),
      sessionsInvalidated: optionalNumber(
        record.sessionsInvalidated,
        'sessionsInvalidated',
        'identities:password-recovery-completed',
      ),
    };
  },
};

export const IDENTITIES_IDENTITY_CREATED_EVENT: EventDefinition<IdentityCreatedPayload> =
  defineEvent<IdentityCreatedPayload>({
    type: 'identities:identity-created',
    version: 1,
    schema: identityCreatedSchema,
    auditable: true,
    description:
      'A new CRDB-backed identity record was created by auth-service; payload contains identifiers only.',
    since: '1.1.0',
  });

export const IDENTITIES_CREDENTIAL_ADDED_EVENT: EventDefinition<CredentialAddedPayload> =
  defineEvent<CredentialAddedPayload>({
    type: 'identities:credential-added',
    version: 1,
    schema: credentialAddedSchema,
    auditable: true,
    description:
      'An authentication credential (password / OTP / magic-link / social / passkey / kiosk PIN / card) was attached to an identity; identifiers only, no secret material.',
    since: '1.1.0',
  });

export const IDENTITIES_PASSWORD_RECOVERY_COMPLETED_EVENT: EventDefinition<PasswordRecoveryCompletedPayload> =
  defineEvent<PasswordRecoveryCompletedPayload>({
    type: 'identities:password-recovery-completed',
    version: 1,
    schema: passwordRecoveryCompletedSchema,
    auditable: true,
    description:
      'A password recovery completed: the credential was reset via a single-use expiring token and prior refresh-token families/sessions were invalidated.',
    since: '1.1.0',
  });

export const IDENTITY_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [IDENTITIES_IDENTITY_CREATED_EVENT.type]: IDENTITIES_IDENTITY_CREATED_EVENT,
  [IDENTITIES_CREDENTIAL_ADDED_EVENT.type]: IDENTITIES_CREDENTIAL_ADDED_EVENT,
  [IDENTITIES_PASSWORD_RECOVERY_COMPLETED_EVENT.type]: IDENTITIES_PASSWORD_RECOVERY_COMPLETED_EVENT,
};
